import { computed, onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumber } from 'libphonenumber-js';
import User from '../models/User';
import PhoneStatus from '../models/PhoneStatus';
import Incident from '../models/Incident';
import { store } from '../store';
import { getErrorMessage } from '../utils/errors';
import Worksite from '../models/Worksite';
import PhoneOutbound from '../models/PhoneOutbound';
import usePhoneService from './phone/usePhoneService';
import useCurrentUser from './useCurrentUser';
import type { PhoneDnisResponse, PhoneDnisResult } from '@/models/types';

export default function useConnectFirst(context: {
  emit: (action: string) => void;
}) {
  const $toasted = useToast();
  const { t } = useI18n();

  const currentAgent = ref<any>(null);
  const dialing = ref(false);

  const { currentUser } = useCurrentUser();

  const languages = computed(() => currentUser?.value?.languages);
  const currentIncident = computed(() => {
    return Incident.find(currentIncidentId.value);
  });
  const currentIncidentId = computed(
    () => store.getters['incident/currentIncidentId'],
  );

  const isTakingCalls = computed(() => store.getters['phone/isTakingCalls']);
  const isNotTakingCalls = computed(
    () => store.getters['phone/isNotTakingCalls'],
  );
  const isOnCall = computed(() => store.getters['phone/isOnCall']);
  const isTransitioning = computed(
    () => store.getters['phone/isTransitioning'],
  );
  const isInboundCall = computed(() => store.getters['phone/isInboundCall']);
  const isOutboundCall = computed(() => store.getters['phone/isOutboundCall']);
  const isConnecting = computed(() => {
    return isTransitioning.value || (isTakingCalls.value && !isOnCall.value);
  });
  const callState = computed(() => store.getters['phone/callState']);
  const callType = computed(() => store.getters['phone/callType']);
  const call = computed(() => store.getters['phone/call']);
  const lastCall = computed(() => store.getters['phone/lastCall']);
  const potentialFailedCall = computed(
    () => store.getters['phone/potentialFailedCall'],
  );
  const currentDnisHistoryRecord = computed(
    () => store.getters['phone/currentDnisHistoryRecord'],
  );
  const caller = computed<PhoneDnisResult>(() => store.getters['phone/caller']);
  const incomingCall = computed(() => store.getters['phone/incomingCall']);
  const outgoingCall = computed(() => store.getters['phone/outgoingCall']);
  const stats = computed(() => store.getters['phone/stats']);
  const agentStats = computed(() => store.getters['phone/agentStats']);
  const gateStats = computed(() => store.getters['phone/gateStats']);
  const callHistory = computed(() => store.getters['phone/callHistory']);
  const phoneService = reactive(usePhoneService());

  const setCallHistory = (callHistory: any) => {
    store.commit('phone/setCallHistory', callHistory);
  };

  const setPotentialFailedCall = (call: any) => {
    store.commit('phone/setPotentialFailedCall', call);
  };

  const setCurrentDnisHistoryRecord = (record: any) => {
    store.commit('phone/setCurrentDnisHistoryRecord', record);
  };

  const setCallType = (callType: any) => {
    store.commit('phone/setCallType', callType);
  };

  const setCaller = (caller: PhoneDnisResponse) => {
    store.commit('phone/setCaller', caller);
  };

  const setOutgoingCall = (call: any) => {
    store.commit('phone/setOutgoingCall', call);
  };

  const setCurrentCall = (call: any) => {
    store.commit('phone/setCurrentCall', call);
  };

  async function setAvailable() {
    return phoneService.changeState('AVAILABLE');
  }

  async function setWorking() {
    return phoneService.changeState('WORKING');
  }

  async function hangUp() {
    return phoneService.hangup();
  }

  async function resetPhoneSystem() {
    return phoneService.resetPhoneSystem();
  }

  async function setAway() {
    return phoneService.changeState('AWAY');
  }

  async function apiGetQueueStats() {
    return phoneService.apiGetQueueStats();
  }

  async function loadAgent() {
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_APP_API_BASE_URL}/phone_agents/me`,
      );
      currentAgent.value = data;
    } catch {
      const { data }: any = await phoneService.createAgent();
      currentAgent.value = data;
    }
  }

  async function logoutPhone() {
    // Await phoneService.changeState('AWAY');
    const agent_id = currentAgent?.value?.agent_id;
    if (agent_id) {
      await phoneService.logout(agent_id);
    }

    await loadAgent();
  }

  async function loginPhone(retry = true, status = 'WORKING') {
    await loadAgent();
    if (!languages?.value?.length) {
      await $toasted.error(t('phoneDashboard.select_language_error'));
      return;
    }

    if (!currentUser?.value?.mobile) {
      await $toasted.error(t('phoneDashboard.add_phone_number_error'));
      return;
    }

    if (phoneService.loggedInAgentId) {
      await setWorking();
      context.emit('onLoggedIn');
    } else {
      const password = import.meta.env.VITE_APP_PHONE_DEFAULT_PASSWORD;

      if (!currentAgent?.value?.agent_username) {
        const { data }: any = await phoneService.createAgent();
        currentAgent.value = data;
      }

      try {
        phoneService.username = currentAgent?.value?.agent_username;
        phoneService.password = password;
        phoneService.agent_id = currentAgent?.value?.agent_id;
        if (!phoneService.cf) {
          const result = await phoneService.getAccessToken();
          phoneService.initPhoneService(result.accessToken);
        }

        await phoneService.login(
          currentAgent?.value?.agent_username,
          password,
          status,
          currentAgent?.value?.agent_id,
        );
        context.emit('onLoggedIn');
      } catch {
        // This.$log.debug(e);
        if (retry) {
          await logoutPhone();
          await loginPhone(false, status);
        }
      }
    }

    phoneService.apiUpdateStats(phoneService.loggedInAgentId).catch(null);
  }

  async function logoutByPhoneNumber() {
    if (currentUser?.value?.mobile) {
      const parsedNumber = parsePhoneNumber(currentUser?.value?.mobile, 'US');
      await Promise.all(
        phoneService.queueIds.map(async (queueId: string) =>
          phoneService
            .apiLoginsByPhone(
              parsedNumber.formatNational().replaceAll(/[^\d.]/g, ''),
              Number(queueId),
            )
            .then(async ({ data }: any) => {
              if (data.length > 0) {
                await Promise.all(
                  data.map(async (login: any) =>
                    phoneService.apiLogoutAgent(login.agentId),
                  ),
                );
                phoneService.initPhoneService();
              }

              return null;
            })
            .catch(null),
        ),
      );
    }
  }

  async function createOutboundCall(outbound: any, number: string) {
    const dnisResponse = await axios.get<PhoneDnisResponse>(
      `${import.meta.env.VITE_APP_API_BASE_URL}/phone_dnis/${outbound.dnis1}`,
    );
    const caller = dnisResponse.data;
    let callType = outbound.call_type.toUpperCase();
    if (callType !== 'CALLDOWN') {
      callType = 'OUTBOUND';
    }

    setCallType(callType);
    setOutgoingCall(outbound);
    setCurrentCall(outbound);
    setCaller(caller);
    await phoneService.dial(
      number,
      Array.isArray(currentIncident?.value?.active_phone_number)
        ? currentIncident?.value?.active_phone_number[0]
        : currentIncident?.value?.active_phone_number,
    );
  }

  async function dialManualOutbound(number: string) {
    const portalResponse = await axios.get(
      `${import.meta.env.VITE_APP_API_BASE_URL}/portals/current`,
      {
        headers: {
          Authorization: null,
        },
      },
    );

    const portal = portalResponse?.data;

    if (portal?.attr?.disable_outbound_calls) {
      $toasted.warning(t('info.outbound_calls_currently_disabled'));
      return;
    }

    await loginPhone(true, 'WORKING');
    dialing.value = true;
    try {
      const outbound = await PhoneOutbound.api().createManual({
        number,
        incidentId: currentIncidentId.value,
        userId: currentUser?.value?.id,
        language: currentUser?.value?.primary_language,
      });
      if (outbound) {
        await createOutboundCall(outbound, number);
      }
    } catch (error) {
      $toasted.error(getErrorMessage(error));
    } finally {
      dialing.value = false;
    }
  }

  async function dialNextOutbound() {
    const portalResponse = await axios.get(
      `${import.meta.env.VITE_APP_API_BASE_URL}/portals/current`,
      {
        headers: {
          Authorization: null,
        },
      },
    );

    const portal = portalResponse?.data;

    if (portal?.attr?.disable_outbound_calls) {
      throw new Error(t('info.outbound_calls_currently_disabled'));
    }

    dialing.value = true;
    try {
      const outbound = await PhoneOutbound.api().getNextOutbound({
        incidentId: currentIncidentId.value,
      });
      if (outbound.worksite) {
        await Worksite.api().fetch(outbound.worksite);
      }

      await createOutboundCall(outbound, outbound.phone_number);
    } finally {
      dialing.value = false;
    }
  }

  async function removeNumberFromQueue(number: string) {
    await PhoneOutbound.api().completeCallsForPhoneNumber(number);
    $toasted.success(t('info.dnis_removed_from_queue'));
  }

  onMounted(async () => {
    const { data } = await axios.get(
      `${import.meta.env.VITE_APP_API_BASE_URL}/phone_agents/call_history`,
    );
    setCallHistory(data);
  });

  return {
    currentAgent,
    dialing,
    currentIncidentId,
    currentIncident,
    loadAgent,
    setAvailable,
    setWorking,
    setAway,
    callState,
    callType,
    call,
    lastCall,
    potentialFailedCall,
    currentDnisHistoryRecord,
    caller,
    incomingCall,
    outgoingCall,
    stats,
    agentStats,
    gateStats,
    languages,
    callHistory,
    isTakingCalls,
    isNotTakingCalls,
    isOnCall,
    isTransitioning,
    isInboundCall,
    isOutboundCall,
    isConnecting,
    dialManualOutbound,
    removeNumberFromQueue,
    hangUp,
    setCurrentIncidentId(id: string) {
      store.commit('incident/setCurrentIncidentId', id);
    },
    setOutgoingCall,
    setIncomingCall(call: any) {
      store.commit('phone/setIncomingCall', call);
    },
    setCurrentCall,
    setCaller,
    setState(state: any) {
      store.commit('phone/setState', state);
    },
    setCallType,
    setCallHistory,
    setPotentialFailedCall,
    setGeneralStats(stats: any) {
      store.commit('phone/setGeneralStats', stats);
    },
    clearCall() {
      store.commit('phone/clearCall');
    },
    resetState() {
      store.commit('phone/resetState');
    },
    loginPhone,
    dialNextOutbound,
    apiGetQueueStats,
    resetPhoneSystem,
  };
}
